import React from 'react';
import { Box, Card, Typography, Avatar, Button, Chip, Fade, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StarIcon from '@mui/icons-material/Star';
import { urlImg, isEmpty } from '../../common/helpers';
import { rolProveedor } from '../../../utils/rolProveedor';

const ProfileProviderCard = ({ profile }) => {
  const navigate = useNavigate();

  const { user, userData, perfil, activeServices, identidadVerificada, serviciosRealizados } = profile;
  const userName = userData?.name || 'Proveedor';

  // Calificación del proveedor = promedio ponderado de sus servicios (por nº de reseñas)
  const serviciosCalificados = (activeServices || []).filter(s => s.ratingPromedio > 0 && s.ratingTotal > 0);
  const totalResenas = serviciosCalificados.reduce((acc, s) => acc + s.ratingTotal, 0);
  const ratingProveedor = totalResenas > 0
    ? serviciosCalificados.reduce((acc, s) => acc + s.ratingPromedio * s.ratingTotal, 0) / totalResenas
    : 0;
  // Su rol o, si no escribió ninguno, las categorías de sus servicios (nada inventado)
  const role = rolProveedor(perfil, activeServices);
  let avatarUrl = '/search.png';
  if (!isEmpty(userData?.avatar)) {
    const avatarStr = userData.avatar.toString();
    if (avatarStr.startsWith("http")) {
      avatarUrl = avatarStr;
    } else if (avatarStr.startsWith("//")) {
      avatarUrl = avatarStr.replace(/^\/\//, "https://");
    } else {
      avatarUrl = `${urlImg}/w_150,h_150,c_fill/users/${user}/${userData.avatar}`;
    }
  }

  // Reservas que completó como proveedor (las cuenta el servidor, como «Servicios realizados» de su perfil; antes salían aquí los
  // servicios que ofrece). Sin ninguna, o si el servidor no lo manda (uno sin actualizar), no se dice nada.
  const realizados = serviciosRealizados > 0
    ? `${serviciosRealizados} ${serviciosRealizados === 1 ? 'servicio realizado' : 'servicios realizados'}`
    : '';

  const handleViewProfile = () => {
    window.location.href = `/perfil/${user}`; // Force true navigation if we want to reset scroll, or use navigate
  };

  return (
    <Card
      elevation={0}
      sx={{
        // Llena su lugar del carrusel (el ancho lo da el carrusel, según cuántas caben por fila) y todas miden lo mismo de alto
        width: '100%',
        height: '100%',
        borderRadius: '8px',
        border: 1, borderColor: 'sutil',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
        }
      }}
    >
      {/* Header section */}
      <Grid container
        sx={{
          height: 95,
          bgcolor: 'secondary.main',
          position: 'relative',
          p: 1.5,
          pt: 2
        }}
      >
        {/* Solo si un administrador aprobó su RFC y su CURP (lo dice el servidor, como en la búsqueda y en el perfil). En el azul
            oscuro: en blanco no se lee sobre el azul medio */}
        {identidadVerificada && (
          <Grid container alignItems="center" sx={{ gap: 0.5, color: 'primary.main' }}>
            <VerifiedUserIcon sx={{ fontSize: 13 }} />
            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '11px' }}>Verificado</Typography>
          </Grid>
        )}

        <Avatar
          src={avatarUrl}
          sx={{
            width: 56,
            height: 56,
            position: 'absolute',
            bottom: -28,
            left: 16,
            border: '3px solid white',
            bgcolor: 'white'
          }}
        />
      </Grid>

      {/* Content section */}
      <Grid container direction="column" sx={{ p: 2, pt: 4, flex: 1 }}>
        {/* Un nombre largo se corta con «…» (completo al pasar el mouse) y la calificación se queda en su lugar */}
        <Grid container wrap="nowrap" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5, gap: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary" noWrap title={userName}
            sx={{ fontSize: '14px', lineHeight: 1.2, minWidth: 0 }}>
            {userName}
          </Typography>
          <Grid container alignItems="center" sx={{ width: 'auto', gap: 0.5, flexShrink: 0 }}>
            {ratingProveedor > 0 ? (
              <>
                <StarIcon sx={{ fontSize: 13, color: 'primary.main' }} />
                <Typography variant="caption" fontWeight="bold" color="text.primary" sx={{ fontSize: "11px" }}>
                  {ratingProveedor.toFixed(1)}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ fontSize: "11px" }}>
                Nuevo
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* A lo más dos líneas (completo al pasar el mouse): un rol largo ya no estira la tarjeta */}
        <Typography variant="caption" color="text.secondary" title={role || undefined}
          sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {role}
        </Typography>

        <Grid container direction="column" sx={{ mb: 3, gap: 1 }}>
          {realizados && (
            <Grid container alignItems="center" sx={{ gap: 1 }}>
              <LocalShippingIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" color="primary" sx={{ fontSize: '12px' }}>
                {realizados}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 'auto' }}>
          {/* Cuántas opiniones suman sus servicios; sin opiniones queda vacío y el botón sigue a la derecha */}
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px' }}>
            {totalResenas > 0 && `${totalResenas} ${totalResenas === 1 ? 'opinión' : 'opiniones'}`}
          </Typography>

          <Button
            variant="contained"
            onClick={handleViewProfile}
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              borderRadius: '4px',
              minWidth: 'auto',
              px: 2,
              py: 0.5,
              fontSize: '11px',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            Ver perfil
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

export default ProfileProviderCard;
